import { useTranslations } from "@/lib/useTranslations";
import { useMemo, useState } from "react";

import { LibraryNavigableRowLink } from "@/components/ui/admin/LibraryNavigableRowLink";

import { defaultLocale } from "@/i18n/config";
import type { ContentStatus } from "@/core/services/types/article.types";
import { getEventSharePath } from "@/core/services/types/event.types";
import { formatEventCalendarDate } from "@/lib/event-dates";
import {
  PLACE_LINKED_EVENT_CATEGORY_FILTERS,
  countPlaceLinkedEventsByCategory,
  filterPlaceLinkedEventsByCategory,
  type PlaceLinkedEvent,
  type PlaceLinkedEventCategoryFilter,
} from "@/core/services/types/place.types";
import type { Enums } from "@/types/database.types";

type EventCategory = Enums<"event_category_enum">;

interface PlaceEventsPanelProps {
  events: PlaceLinkedEvent[];
}

function formatDateRange(startDate: string | null, endDate: string | null): string {
  if (!startDate && !endDate) return "—";

  const start = startDate ? formatEventCalendarDate(startDate, defaultLocale) : null;
  const end = endDate ? formatEventCalendarDate(endDate, defaultLocale) : null;

  if (start && end) {
    return start === end ? start : `${start} – ${end}`;
  }

  return start ?? end ?? "—";
}

function EventCategoryLabel({
  category,
}: {
  category: EventCategory | null;
}) {
  const t = useTranslations("places.edit.events");

  if (!category) {
    return (
      <span className="text-neutral-400">{t("categoryUnassigned")}</span>
    );
  }

  return <span className="text-neutral-600">{t(`eventCategories.${category}`)}</span>;
}

function EventStatusBadge({ status }: { status: ContentStatus }) {
  const t = useTranslations("places.library.statuses");

  const styles: Record<ContentStatus, string> = {
    SIGNAL: "bg-violet-50 text-violet-700",
    DRAFT: "bg-neutral-100 text-neutral-600",
    PENDING_REVIEW: "bg-amber-50 text-amber-700",
    SCHEDULED: "bg-amber-50 text-amber-800",
    PUBLISHED: "bg-emerald-50 text-emerald-700",
    ARCHIVED: "bg-neutral-100 text-neutral-500",
  };

  return (
    <span
      className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${styles[status]}`}
    >
      {t(status)}
    </span>
  );
}

export function PlaceEventsPanel({ events }: PlaceEventsPanelProps) {
  const t = useTranslations("places.edit.events");
  const [activeCategory, setActiveCategory] =
    useState<PlaceLinkedEventCategoryFilter>("ALL");

  const categoryCounts = useMemo(
    () => countPlaceLinkedEventsByCategory(events),
    [events],
  );
  const filteredEvents = useMemo(
    () => filterPlaceLinkedEventsByCategory(events, activeCategory),
    [events, activeCategory],
  );

  return (
    <section className="wireframe-border bg-whart-bg">
      {events.length > 0 ? (
        <nav
          aria-label={t("categoryNav")}
          className="border-b border-black/10"
        >
          <ul className="flex items-center gap-1 overflow-x-auto px-4">
            {PLACE_LINKED_EVENT_CATEGORY_FILTERS.map((categoryFilter) => {
              const isActive = activeCategory === categoryFilter;

              return (
                <li key={categoryFilter} className="shrink-0">
                  <button
                    type="button"
                    onClick={() => setActiveCategory(categoryFilter)}
                    className={`inline-flex border-b-2 px-3 py-3 text-sm transition-colors ${isActive
                        ? "border-whart-dark font-medium text-whart-text"
                        : "border-transparent text-neutral-600 hover:border-black/10 hover:text-whart-text"
                      }`}
                    aria-current={isActive ? "page" : undefined}
                  >
                    {t(`categoryFilters.${categoryFilter}`)}
                    <span className="ml-1.5 text-neutral-400">
                      ({categoryCounts[categoryFilter]})
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      ) : null}

      <div className="border-b border-black/10 px-4 py-3">
        <p className="text-sm text-neutral-600">
          {t("eventCount", { count: filteredEvents.length })}
        </p>
      </div>

      {events.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-neutral-500">
          {t("empty")}
        </p>
      ) : filteredEvents.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-neutral-500">
          {t("emptyFilter")}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[40rem] table-fixed text-left text-sm">
            <colgroup>
              <col />
              <col className="w-32" />
              <col className="w-52" />
              <col className="w-32" />
            </colgroup>
            <thead>
              <tr className="wireframe-border-b bg-neutral-50 text-xs font-medium uppercase tracking-wider text-neutral-500">
                <th className="px-4 py-3 text-left">{t("titleColumn")}</th>
                <th className="px-4 py-3 text-left">{t("typeColumn")}</th>
                <th className="px-4 py-3 text-left">{t("datesColumn")}</th>
                <th className="px-4 py-3 text-left">{t("statusColumn")}</th>
              </tr>
            </thead>
            {filteredEvents.map((event) => {
              const editHref = getEventSharePath(event.id);

              return (
                <tbody
                  key={event.id}
                  className="group relative cursor-pointer border-b border-neutral-100 hover:bg-neutral-50"
                >
                  <tr>
                    <td className="relative px-4 py-3 font-medium text-whart-text">
                      <LibraryNavigableRowLink
                        to={editHref}
                        ariaLabel={t("editEvent", { title: event.title })}
                      />
                      <span className="relative z-0 underline-offset-2 group-hover:underline">
                        {event.title}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <EventCategoryLabel category={event.event_category} />
                    </td>
                    <td className="px-4 py-3 text-neutral-600">
                      {formatDateRange(event.start_date, event.end_date)}
                    </td>
                    <td className="px-4 py-3">
                      <EventStatusBadge status={event.status} />
                    </td>
                  </tr>
                </tbody>
              );
            })}
          </table>
        </div>
      )}
    </section>
  );
}
